<script setup>
import { reactive, ref, defineProps, defineEmits, watch, onMounted } from "vue";
import {
  createProject,
  editProject,
  getProjectDetail,
  getProjects
} from "@/services/projects";
import { getChannels } from "@/services/channels";
import CreatedProyect from "@/components/Proyects/CreatedProyect.vue";
import { CheckCircleIcon, TrashIcon } from "@heroicons/vue/24/solid";
import * as bootstrap from 'bootstrap';

const showPriorityManagement = ref(false);
const communicationTypeRef = ref(null);
const scoreRef = ref(null);
const codeRef = ref(null);
const communicationsList = ref([]);

const channelsList = ref([]);
const channelNameRef = ref(null);
const channelDescriptionRef = ref(null);
const existingChannels = ref([]);
const selectedExistingChannel = ref(null);
const channelSelectionMode = ref('new'); // 'new' or 'existing'

const projectCreated = ref(false);
const timesContactDaily = ref();
const isEditMode = ref(false);

const emit = defineEmits(["updateProject"]);

const props = defineProps({
  projectData: Object,
});

const projectData = reactive({
  name: "",
  limitPoints: "",
  communications: [],
  channels: [],
});

const step = ref(1);

/* function handleNextStep() {
  step.value++;
}

function handlePreviousStep() {
  step.value--;
} */

const togglePriority = (value) => {
  if (value) {
    showPriorityManagement.value = true;
  } else {
    showPriorityManagement.value = false;
    projectData.limitPoints = null;
    communicationsList.value = [];
  }
};

//Edicion
watch(
  () => props.projectData,
  async (newVal) => {
    if (newVal && Object.keys(newVal).length > 0) {
      const projectsId = newVal.projectsId;

      if (projectsId) {
        try {
          const projectDetail = await getProjectDetail(projectsId);
          console.log("Detalles del proyecto:", projectDetail.data);

          Object.assign(projectData, {
            name: projectDetail.data.name || "",
            limitPoints: projectDetail.data.limitPoints || "",
          });
          timesContactDaily.value = projectDetail.data.timesContactDaily || "";

          communicationsList.value = (
            projectDetail.data.communications || []
          ).map((communication) => ({
            type: communication.name,
            score: communication.points,
            code: communication.acronym,
          }));

          channelsList.value = projectDetail.data.channels || [];

          showPriorityManagement.value = !!projectDetail.data.limitPoints;

          isEditMode.value = true;
        } catch (error) {
          console.error("Error al obtener los detalles del proyecto:", error);
        }
      } else {
        console.log("No se encontró un id en projectData");
        resetForm();
      }
    } else {
      isEditMode.value = false;
      resetForm();
    }
  },
  { immediate: true }
);

//Validacion
const isSubmitting = ref(false);

const stepErrors = reactive({
  step1: false,
  step2: false,
  step3: false,
});

function validateStep1() {
  if (!projectData.name) {
    stepErrors.step1 = true;
    return false;
  }
  stepErrors.step1 = false;
  return true;
}

function validateStep2() {
  if (channelsList.value.length === 0) {
    stepErrors.step2 = true;
    return false;
  }
  stepErrors.step2 = false;
  return true;
}

function validateStep3() {
  // No hay campos obligatorios en el paso 3
  stepErrors.step3 = false;
  return true;
}
function handleNextStep() {
  if (step.value === 1 && validateStep1()) {
    step.value++;
  } else if (step.value === 2 && validateStep2()) {
    step.value++;
  } else if (step.value === 3) {
    validateStep3(); // no tiene validación obligatoria
  }
}

function handlePreviousStep() {
  step.value--;
}

async function handleSubmitProject() {
  if (isSubmitting.value || !projectData.name) {
    return;
  }

  isSubmitting.value = true;

  const channels = channelsList.value.length ? channelsList.value : [];

  const payload = {
    projectId: isEditMode.value ? props.projectData.projectsId : undefined,
    name: projectData.name,
    clientId: localStorage.getItem("cx"),
    timesContactDaily: timesContactDaily.value,
    limitPoints: showPriorityManagement.value ? projectData.limitPoints : null, // Solo envía limitPoints si está activado
    communicationsList: showPriorityManagement.value
      ? communicationsList.value
      : [], // Solo envía las comunicaciones si está activado
    channels: channels,
  };

  console.log("Payload enviado:", payload);

  try {
    if (isEditMode.value) {
      const response = await editProject(payload);
      emit("updateProject", response.data);
      resetForm();
    } else {
      await createProject(payload);
      projectCreated.value = true;
      resetForm();
    }
  } catch (error) {
    if (error.response) {
      console.error(
        "Error en la creación/edición del proyecto:",
        error.response
      );
    } else {
      console.error("Error en la creación/edición del proyecto:", error);
      console.error("Detalles del error:", error.response.data);
    }
  } finally {
    isSubmitting.value = false;
  }
}

function resetForm() {
  projectData.name = "";
  timesContactDaily.value = "";
  projectData.limitPoints = "";
  communicationsList.value = [];
  channelsList.value = [];
  showPriorityManagement.value = false;
  step.value = 1;
}

function addCommunication() {
  if (communicationTypeRef.value && scoreRef.value && codeRef.value) {
    const communication = {
      type: communicationTypeRef.value.value,
      score: parseInt(scoreRef.value.value),
      code: codeRef.value.value,
    };

    if (communication.type && communication.score && communication.code) {
      communicationsList.value.push(communication);
      console.log("Comunicación agregada:", communication);
    } else {
      console.warn("Faltan datos en la comunicación");
    }
    // Limpiar los inputs
    communicationTypeRef.value.value = "";
    scoreRef.value.value = "";
    codeRef.value.value = "";
  }
}
function removeCommunication(index) {
  communicationsList.value.splice(index, 1);
}

function addChannel() {
  if (channelNameRef.value && channelDescriptionRef.value) {
    const channel = {
      name: channelNameRef.value.value,
      description: channelDescriptionRef.value.value,
      clientsId: localStorage.getItem("cx"),
    };

    if (!Array.isArray(channelsList.value)) {
      channelsList.value = [];
    }

    channelsList.value.push(channel);
    // Limpiar los inputs
    channelNameRef.value.value = "";
    channelDescriptionRef.value.value = "";
    console.log("Canal agregado:", channel);
  }
}
console.log("Canales agregados", channelsList.value);

function removeChannel(index) {
  channelsList.value.splice(index, 1);
}

// Add this function to fetch existing channels
async function fetchExistingChannels() {
  try {
    // Use the getChannels service to fetch existing channels directly
    const response = await getChannels(localStorage.getItem("cx"));
    console.log("Canales existentes", response.data);
    
    if (response && response.data) {
      // Map the channels to the expected format
      const allChannels = response.data.map(channel => ({
        id: channel.id || channel.channelsId,
        name: channel.name,
        description: channel.description || `Canal de ${channel.name}`
      }));
      
      // Set the existingChannels ref with the channels
      existingChannels.value = allChannels;
      
      console.log('Canales existentes cargados:', existingChannels.value);
    }
  } catch (error) {
    console.error('Error al obtener canales existentes:', error);
    // Set empty array in case of error
    existingChannels.value = [];
  }
}

// Add this function to add an existing channel
function addExistingChannel() {
  if (selectedExistingChannel.value) {
    const channel = {
      name: selectedExistingChannel.value.name,
      description: selectedExistingChannel.value.description,
      clientsId: localStorage.getItem("cx"),
    };

    if (!Array.isArray(channelsList.value)) {
      channelsList.value = [];
    }

    channelsList.value.push(channel);
    selectedExistingChannel.value = null;
    console.log("Canal existente agregado:", channel);
  }
}

// Call fetchExistingChannels when component is mounted
fetchExistingChannels();

// Add a watch function to generate code when communication type changes
watch(communicationTypeRef, (newVal) => {
  if (newVal && newVal.value) {
    // Generate a code based on the first 4 characters of the communication type
    // Convert to uppercase and remove spaces
    const code = newVal.value
      .substring(0, 4)
      .toUpperCase()
      .replace(/\s+/g, '');
    
    // If codeRef exists, set its value
    if (codeRef.value) {
      codeRef.value.value = code;
    }
  }
}, { immediate: true });

// Add a direct event handler for the communication type input
function updateCode(event) {
  if (event && event.target) {
    const value = event.target.value;
    if (value) {
      // Generate a code based on the first 4 characters of the communication type
      // Convert to uppercase and remove spaces
      const code = value
        .substring(0, 4)
        .toUpperCase()
        .replace(/\s+/g, '');
      
      // If codeRef exists, set its value
      if (codeRef.value) {
        codeRef.value.value = code;
      }
    }
  }
}

// Add this function to initialize tooltips after the component is mounted
onMounted(() => {
  // Initialize Bootstrap tooltips
  const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
  tooltipTriggerList.map(function (tooltipTriggerEl) {
    return new bootstrap.Tooltip(tooltipTriggerEl);
  });
});

// Existing onMounted calls...
onMounted(fetchExistingChannels);
</script>
<template>
  <div>
    <article>
      <h2 class="create-title text-capitalize fs-3 mb-4">
        {{ isEditMode ? `Editar Proyecto: ${projectData.name}` : "" }}
      </h2>
      <div class="stepper-form-layout">
        <div class="stepper-vertical stepper-bg">
          <div
            class="step-vertical"
            :class="{ active: step === 1, completed: step > 1 }"
          >
            <div class="step-circle" :class="{ selected: step === 1, completed: step > 1 }">
              <template v-if="step > 1">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" style="width: 1.3rem; height: 1.3rem; color: #fff;">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </template>
              <template v-else>1</template>
            </div>
            <span class="step-label" :class="{ active: step === 1, completed: step > 1 }">Datos</span>
          </div>
          <div
            class="step-vertical"
            :class="{ active: step === 2, completed: step > 2 }"
          >
            <div class="step-circle" :class="{ selected: step === 2, completed: step > 2 }">
              <template v-if="step > 2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" style="width: 1.3rem; height: 1.3rem; color: #fff;">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </template>
              <template v-else>2</template>
            </div>
            <span class="step-label" :class="{ active: step === 2, completed: step > 2 }">Canales</span>
          </div>
          <div
            class="step-vertical"
            :class="{ active: step === 3 }"
          >
            <div class="step-circle" :class="{ selected: step === 3 }">3</div>
            <span class="step-label" :class="{ active: step === 3 }">Prioridades</span>
          </div>
        </div>
        <div class="stepper-form-content">
          <section v-if="step === 1">
            <form @submit.prevent="handleNextStep" style="margin-top: 5%">
              <p
                class="form-label globals-label"
                style="font-weight: 800; text-align: start; font-size: x-large"
              >
                Captura los datos del Proyecto
              </p>
              <div class="mb-4">
                <label
                  for="projectTitle"
                  class="form-label globals-label"
                  style="font-size: 16px"
                  >Nombre</label
                >
                <input
                  type="text"
                  class="form-control globals-input"
                  id="projectTitle"
                  placeholder="Título"
                  v-model="projectData.name"
                />
              </div>
              <div class="d-flex justify-content-center">
                <p v-if="stepErrors.step1" class="text-danger m-0">
                  El Nombre es obligatorio.
                </p>
              </div>
              <div class="d-flex justify-content-end my-2">
                <button class="stepper-nav-btn next" @click="handleNextStep" type="button">
                  Continuar
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                </button>
              </div>
            </form>
          </section>

          <section v-if="step === 2">
            <form @submit.prevent="handleNextStep" style="margin-top: 5%">
              <div class="d-flex flex-row gap-4 my-4">
                <div class="flex-grow-1">
                  <div class="d-flex flex-column justify-content-start w-100">
                    <p
                      class="form-label globals-label"
                      style="font-weight: 800; text-align: start; font-size: x-large"
                    >
                      Asignar Canales
                    </p>
                    <p
                      class="form-label globals-label"
                      style="font-weight: 600; text-align: start"
                    >
                      Crea un nuevo canal y asígnalo a tu proyecto.
                    </p>

                    <!-- Límite diario moved from step 1 -->
                    <div class="mb-4">
                      <label
                        for="exampleInputEmail3"
                        class="form-label globals-label pt-2 text-nowrap"
                        style="width: 50%; font-size: 16px"
                      >Límite <span style="text-decoration: underline">diario</span> de
                        contactabilidad por todos los canales:</label
                      >
                      <input
                        type="number"
                        id="times"
                        v-model="timesContactDaily"
                        class="form-control form-select-details"
                        style="width: 50%"
                        min="1"
                        max="5"
                      />
                      <span class="note-text">*El límite diario es opcional</span>
                    </div>

                    <!-- Channel selection mode toggle -->
                    <div class="mb-4 custom-radio-group">
                      <label class="custom-radio-pill" :class="{ selected: channelSelectionMode === 'new' }">
                        <input type="radio" class="custom-radio-input" value="new" v-model="channelSelectionMode" />
                        <span class="custom-radio-circle"></span>
                        <span class="custom-radio-label">Agregar nuevo canal</span>
                      </label>
                      <label class="custom-radio-pill" :class="{ selected: channelSelectionMode === 'existing' }" v-if="existingChannels.length > 0">
                        <input type="radio" class="custom-radio-input" value="existing" v-model="channelSelectionMode" />
                        <span class="custom-radio-circle"></span>
                        <span class="custom-radio-label">Seleccionar canal existente</span>
                      </label>
                    </div>

                    <!-- *Agregar nuevo canal -->
                    <section class="base mt-4" v-if="channelSelectionMode === 'new'">
                      <form @submit.prevent class="w-100 p-3">
                        <p
                          class="form-label globals-label"
                          style="font-weight: 600; text-align: start"
                        >
                          Agregar nuevo Canal
                        </p>
                        <div>
                          <div class="mb-3">
                            <label
                              class="form-label globals-label"
                              style="font-size: 16px"
                              >Nombre del Canal</label
                            >
                            <input
                              class="form-control globals-input"
                              id="projectTitle"
                              placeholder="Nombre del canal"
                              :ref="(el) => (channelNameRef = el)"
                            />
                          </div>
                          <div>
                            <label
                              for="exampleInputEmail2"
                              class="form-label globals-label"
                              style="font-size: 16px"
                              >Descripción</label
                            >
                            <div class="mb-3 d-flex gap-2">
                              <textarea
                                type="text"
                                class="form-control globals-input"
                                id="exampleInputEmail2"
                                placeholder=""
                                :ref="(el) => (channelDescriptionRef = el)"
                              />
                            </div>
                          </div>
                        </div>

                        <div class="d-flex justify-content-center my-2">
                          <button
                            class="globals-border-button"
                            style="
                              background: transparent;
                              font-weight: 600;
                              text-wrap: nowrap;
                              width: auto;
                            "
                            @click="addChannel"
                          >
                            Guardar y Agregar Nuevo Canal
                          </button>
                        </div>
                      </form>
                    </section>

                    <!-- Seleccionar canal existente -->
                    <section class="base mt-4" v-if="channelSelectionMode === 'existing' && existingChannels.length > 0">
                      <form @submit.prevent class="w-100 p-3">
                        <p
                          class="form-label globals-label"
                          style="font-weight: 600; text-align: start"
                        >
                          Seleccionar Canal Existente
                        </p>
                        <div class="mb-3">
                          <label
                            class="form-label globals-label"
                            style="font-size: 16px"
                            >Canales disponibles</label
                          >
                          <select 
                            class="form-control globals-input" 
                            v-model="selectedExistingChannel"
                          >
                            <option value="" disabled>Selecciona un canal</option>
                            <option 
                              v-for="channel in existingChannels" 
                              :key="channel.id" 
                              :value="channel"
                            >
                              {{ channel.name }}
                            </option>
                          </select>
                        </div>

                        <div class="d-flex justify-content-center my-2">
                          <button
                            class="globals-border-button"
                            style="
                              background: transparent;
                              font-weight: 600;
                              text-wrap: nowrap;
                              width: auto;
                            "
                            @click="addExistingChannel"
                            :disabled="!selectedExistingChannel"
                          >
                            Agregar Canal Seleccionado
                          </button>
                        </div>
                      </form>
                    </section>
                  </div>
                </div>
                <div class="channels-list-column" style="width: 300px; background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); padding: 20px;">
                  <div v-if="stepErrors.step2" class="error-message">
                    <p>Debes agregar al menos un canal.</p>
                  </div>
                  <div class="channels-list-container" v-if="channelsList.length > 0">
                    <p class="form-label globals-label channels-list-title" style="font-weight: 600; text-align: start; margin-top: 1rem">Canales Agregados:</p>
                    <div id="dataList" class="channels-list">
                      <div v-for="(channel, index) in channelsList" :key="index" class="channel-item">
                        <div class="channel-item-header">
                          <div class="channel-icon">
                            <CheckCircleIcon class="text-blue-500" style="color: #517fa4; width: 20px; height: 20px"></CheckCircleIcon>
                          </div>
                          <h5 class="channel-name">{{ channel.name }}</h5>
                          <button class="channel-delete-button" @click="removeChannel(index)" type="button" title="Eliminar canal">
                            <TrashIcon style="width: 18px; height: 18px"></TrashIcon>
                          </button>
                        </div>
                        <div class="channel-description">
                          <p>{{ channel.description }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="channels-list-container empty-channels-list" v-else>
                    <p class="empty-message">No hay canales agregados. Agrega al menos un canal para continuar.</p>
                  </div>
                </div>
              </div>
              
              <div class="d-flex justify-content-evenly my-4 pb-6">
                <button class="stepper-nav-btn prev" @click="handlePreviousStep" type="button">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                  Volver
                </button>
                <button class="stepper-nav-btn next" @click="handleNextStep" type="button">
                  Continuar
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                </button>
              </div>
            </form>
          </section>

          <section v-if="step === 3" style="height: auto; overflow: auto">
            <form @submit.prevent="handleSubmitProject" style="margin-top: 5%">
              <div class="d-flex flex-row gap-4 my-4">
                <div class="flex-grow-1">
                  <p class="form-label globals-label" style="font-weight: 800; text-align: start; font-size: x-large">
                    Gestor de Prioridades
                  </p>
                  <div class="priority-info-box">
                    <h6 class="priority-info-title">¿Cómo funciona el Presupuesto de Puntos?</h6>
                    <p class="priority-info-text">
                      Cada proyecto tiene un límite diario de puntos de comunicación. Cada mensaje que envíes (email, SMS, push, etc.) consume una cantidad de puntos que defines manualmente según su tipo o criticidad.
                    </p>
                    <p class="priority-info-text">
                      Cuando se alcanza el límite diario del proyecto, las comunicaciones de menor prioridad (mayor puntuación) se descartan automáticamente. Esta lógica te ayuda a priorizar mensajes clave y evitar la sobrecomunicación.
                    </p>
                  </div>
                  <div class="py-3">
                    <label for="exampleInputEmail2" class="form-label globals-label" style="font-size: 16px">Presupuesto de Puntos</label>
                    <div class="mb-4 custom-radio-group">
                      <label class="custom-radio-pill" :class="{ selected: !showPriorityManagement }">
                        <input type="radio" class="custom-radio-input" :checked="!showPriorityManagement" @change="togglePriority(false)" />
                        <span class="custom-radio-circle"></span>
                        <span class="custom-radio-label">No</span>
                      </label>
                      <label class="custom-radio-pill" :class="{ selected: showPriorityManagement }">
                        <input type="radio" class="custom-radio-input" v-model="showPriorityManagement" :checked="showPriorityManagement" @change="togglePriority(true)" />
                        <span class="custom-radio-circle"></span>
                        <span class="custom-radio-label">Si</span>
                      </label>
                    </div>
                    <div class="d-flex flex-column align-items-center justify-content-center gap-4 my-4" v-if="showPriorityManagement">
                      <div class="d-flex flex-column justify-content-start w-100">
                        <div>
                          <label for="exampleInputEmail3" class="form-label globals-label pt-2 text-nowrap" style="width: auto; font-size: 16px">Límite <span style="text-decoration: underline">diario</span> de puntos:
                            <i class="fas fa-info-circle score-info-icon" data-bs-toggle="tooltip" data-bs-placement="top" title="Este es el máximo de puntos que puedes consumir por día. Se reinicia cada 24 horas. No se acumulan los puntos no utilizados."></i>
                          </label>
                          <input type="number" id="times" class="form-control form-select-details w-50" style="width: 100%" v-model="projectData.limitPoints" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="showPriorityManagement" class="communications-flex-row" style="display: flex; flex-direction: row; gap: 2rem; align-items: flex-start; width: 100%; margin-bottom: 1.5rem;">
                    <section class="base" style="flex: 1;">
                      <form @submit.prevent class="w-100 p-3">
                        <p class="form-label globals-label" style="font-weight: 600; text-align: start">Comunicaciones</p>
                        <div>
                          <label for="exampleInputEmail3" class="form-label globals-label pt-2 text-nowrap" style="width: auto; font-size: 16px">Nombre de la comunicación:</label>
                          <input for="communicationType" type="text" id="times" class="form-control form-select-details" style="width: 100%" :ref="(el) => (communicationTypeRef = el)" placeholder="Ej: Llamada telefónica" @input="updateCode" />
                        </div>
                        <div>
                          <label for="exampleInputEmail3" class="form-label globals-label pt-2 text-nowrap" style="width: auto; font-size: 16px">Puntaje:
                            <i class="fas fa-info-circle score-info-icon" data-bs-toggle="tooltip" data-bs-placement="top" title="Define cuántos puntos cuesta enviar este tipo de mensaje. Comunicaciones con menor puntaje tienen más prioridad cuando el presupuesto es limitado."></i>
                          </label>
                          <input for="score" type="number" id="times" class="form-control form-select-details" style="width: 100%" :ref="(el) => (scoreRef = el)" />
                        </div>
                        <div>
                          <label for="exampleInputEmail3" class="form-label globals-label pt-2 text-nowrap" style="width: auto; font-size: 16px">Código:</label>
                          <input for="code" type="text" id="times" class="form-control form-select-details" style="width: 100%" min="1" max="4" oninput="if(this.value.length > 4) this.value = this.value.slice(0, 4);" :ref="(el) => (codeRef = el)" readonly />
                          <span class="note-text">*Código generado automáticamente (máximo 4 caracteres)</span>
                        </div>
                        <div class="d-flex justify-content-center my-2">
                          <button class="globals-border-button" style="background: transparent; font-weight: 600; text-wrap: nowrap; width: auto;" @click="addCommunication">
                            Guardar y Agregar Nueva
                          </button>
                        </div>
                      </form>
                    </section>
                    <div class="communications-list-column" style="width: 300px; background: #fff; border-radius: 10px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); padding: 20px;">
                      <p class="form-label globals-label" style="font-weight: 600; text-align: start; margin-top: 1rem">Comunicaciones agregadas:</p>
                      <div class="communications-list-container">
                        <div v-if="communicationsList.length === 0" class="empty-communications-list">
                          <p class="empty-message">No hay comunicaciones agregadas. Agrega al menos una comunicación para continuar.</p>
                        </div>
                        <div v-else class="communications-list">
                          <div v-for="(communication, index) in communicationsList" :key="index" class="communication-item">
                            <div class="communication-item-header">
                              <div class="communication-icon">
                                <CheckCircleIcon class="text-blue-500" style="color: #517fa4; width: 20px; height: 20px"></CheckCircleIcon>
                              </div>
                              <h5 class="communication-name">{{ communication.type }}</h5>
                              <button class="communication-delete-button" @click="removeCommunication(index)" type="button" title="Eliminar comunicación">
                                <TrashIcon style="width: 18px; height: 18px"></TrashIcon>
                              </button>
                            </div>
                            <div class="communication-details">
                              <div class="detail-item">
                                <span class="detail-label">Puntaje del mensaje:
                                  <i class="fas fa-info-circle score-info-icon" data-bs-toggle="tooltip" data-bs-placement="top" title="Define cuántos puntos cuesta enviar este tipo de mensaje. Comunicaciones con menor puntaje tienen más prioridad cuando el presupuesto es limitado."></i>
                                </span>
                                <span class="detail-value">{{ communication.score }}</span>
                              </div>
                              <div class="detail-item">
                                <span class="detail-label">Código:</span>
                                <span class="detail-value code-badge">{{ communication.code }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-evenly my-4">
                <button class="stepper-nav-btn prev" @click="handlePreviousStep" type="button">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
                  Volver
                </button>
                <button class="stepper-nav-btn next" @click="handleSubmitProject" :disabled="isSubmitting" type="submit">
                  {{ isEditMode ? "Guardar Cambios" : "Crear Proyecto" }}
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </article>
    <div v-if="projectCreated" class="modal-background">
      <div class="modal-content">
        <CreatedProyect></CreatedProyect>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import "@/_scss/global/_variables.scss";
::-webkit-scrollbar {
  width: 5px; /* scrollbar */
  height: 10px;
  background-color: $fourth-color;
}

::-webkit-scrollbar-thumb {
  background-color: $seven-color; /* color del thumb (la parte que se mueve) */
  border-radius: 10px;
}

::-webkit-scrollbar-track {
  background-color: $fourth-color; /* fondo del track (la parte que no se mueve) */
}
.step.active {
  width: 30%;
  height: 100%;
  transition: background-color 0.8s ease-in-out;
}
.step::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 0; /* ancho inicial */
  height: 100%;
  transition: width 0.5s ease-in-out;
}
.step:first-child.active::before {
  border-radius: 20px 0 0 20px;
}

.step:not(:first-child):not(:last-child).active::before {
  border-radius: 0;
}

.step:last-child.active::before {
  border-radius: 0 20px 20px 0;
}
.step.active::before {
  width: 100%;
}

.step {
  display: inline-flex;
  color: $primary-color;
  height: 100%;
  gap: 8px;
  position: relative;
  padding: 3px 15px;
  width: 100%;
  background: transparent;
}
.stepper {
  /* border: 2px solid black; */
  background: $white-color;
  box-shadow: 0 4px 6px $shadow-color;
  /* border: 1px solid rgba(213, 213, 213, 0.489); */
  border-radius: 20px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 80%;
}
.note-text {
  color: $six-color;
  font-size: 0.7em;
}
.grid-container {
  display: grid;
  grid-template-columns: repeat(3, minmax(150px, 1fr));
  gap: 10px;
}
.modal-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: $shadow-color;
  z-index: 99;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  display: flex;
  align-items: center;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 50%;
  margin-left: 3.5rem;
  z-index: 100;
  background: $ultra-white-color;
  padding: 50px;
  border-radius: 5px;
  box-shadow: 0 2px 5px $shadow-color;
}
.base {
  display: flex;
  background-color: $white-color;
  border-radius: 15px;
  box-shadow: 0 4px 6px $shadow-color;
  padding: 10px;
  width: 70%;
}
.buttons-group {
  border: none;
  background: transparent;
  cursor: pointer;
}

.channels-list-container {
  background-color: $white-color;
  border-radius: 10px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  width: 100%;
}

.channels-list-title {
  font-size: 1.1rem;
  color: $primary-color;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.empty-channels-list {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px 0;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  margin: 10px 0;
}

.empty-message {
  color: $six-color;
  font-style: italic;
  text-align: center;
}

.channels-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.channel-item {
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
}

.channel-item-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.channel-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
}

.channel-name {
  flex-grow: 1;
  font-weight: 600;
  color: $primary-color;
  margin: 0;
  font-size: 1rem;
}

.channel-delete-button {
  background: transparent;
  border: none;
  color: $six-color;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(220, 53, 69, 0.1);
    color: #dc3545;
  }
}

.channel-description {
  color: $six-color;
  font-size: 0.9rem;
  line-height: 1.4;
  padding-left: 30px;
}

@media (max-width: 768px) {
  .channel-item {
    padding: 12px;
  }
  
  .channel-description {
    padding-left: 15px;
  }
}

.communications-list-container {
  background-color: $white-color;
  border-radius: 10px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  width: 100%;
}

.communications-list-title {
  font-size: 1.1rem;
  color: $primary-color;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.empty-communications-list {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px 0;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  margin: 10px 0;
}

.empty-message {
  color: $six-color;
  font-style: italic;
  text-align: center;
}

.communications-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.communication-item {
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
}

.communication-item-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.communication-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
}

.communication-name {
  flex-grow: 1;
  font-weight: 600;
  color: $primary-color;
  margin: 0;
  font-size: 1rem;
}

.communication-delete-button {
  background: transparent;
  border: none;
  color: $six-color;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(220, 53, 69, 0.1);
    color: #dc3545;
  }
}

.communication-details {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  padding-left: 30px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-label {
  color: $six-color;
  font-size: 0.9rem;
}

.detail-value {
  font-weight: 600;
  color: $primary-color;
}

.code-badge {
  background-color: rgba(81, 127, 164, 0.1);
  color: #517fa4;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .communication-item {
    padding: 12px;
  }
  
  .communication-details {
    padding-left: 15px;
  }
}

.priority-info-box {
  background-color: rgba(81, 127, 164, 0.08);
  border-left: 4px solid #517fa4;
  border-radius: 6px;
  padding: 16px 20px;
  margin: 15px 0 25px;
}

.priority-info-title {
  font-weight: 600;
  color: #243949;
  margin-bottom: 12px;
  font-size: 1rem;
}

.priority-info-text {
  color: #4a5568;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 10px;
}

.priority-info-text:last-child {
  margin-bottom: 0;
}

.score-info-icon {
  color: #517fa4;
  margin-left: 4px;
  font-size: 0.9rem;
  cursor: help;
  transition: color 0.2s ease;
}

.score-info-icon:hover {
  color: #243949;
}

// NUEVO STEPPER VERTICAL
.stepper-vertical {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 2.5rem;
  margin-top: 1.5rem;
}
.step-vertical {
  display: flex;
  align-items: center;
  gap: 1.1rem;
  width: 100%;
  margin-bottom: 0.7rem;
}
.step-circle {
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  border-radius: 50%;
  background: #fff;
  color: #355E77;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  border: 2px solid #355E77;
  transition: all 0.2s;
  line-height: 1;
  text-align: center;
  box-sizing: border-box;
}
.step-label {
  flex: 1;
  font-family: Mulish;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0%;

  color: #355E77;
  background: #fff;
  border-radius: 10px;
  padding: 0.3rem 2.2rem 0.3rem 1.1rem;
  border: 2px solid #355E77;
  transition: all 0.2s;
  text-align: left;
  display: flex;
  align-items: center;
  min-height: 2.1rem;
}
.step-vertical.active .step-circle {
  background: #355E77;
  color: #fff;
  border: 2px solid #355E77;
}
.step-vertical.completed .step-circle {
  background: #357A3B;
  color: #fff;
  border: 2px solid #357A3B;
}
.step-vertical.active .step-label {
  background: #355E77;
  color: #fff;
  border: 2px solid #355E77;
}
.step-vertical.completed .step-label {
  background: #fff;
  color: #357A3B;
  border: 2px solid #357A3B;
}

// Layout de stepper y formulario
.stepper-form-layout {
  display: flex;
  flex-direction: row;
  gap: 2.5rem;
  align-items: flex-start;
  width: 100%;
}
.stepper-bg {
  background: $seven-color;
  border-radius: 18px;
  padding: 2.5rem 2rem;
  min-width: 220px;
  min-height: 350px;
  box-shadow: 0 2px 8px rgba(33,112,123,0.06);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
}
.stepper-form-content {
  flex: 1;
  background: transparent;
  border-radius: 18px;
  padding: 0 0 0 0.5rem;
}
@media (max-width: 900px) {
  .stepper-form-layout {
    flex-direction: column;
    gap: 1.5rem;
  }
  .stepper-bg {
    flex-direction: row;
    min-width: unset;
    min-height: unset;
    width: 100%;
    justify-content: flex-start;
    padding: 1.2rem 0.5rem;
  }
  .stepper-form-content {
    padding: 0;
  }
  .step-vertical {
    width: 100%;
  }
}
.custom-radio-group {
  display: flex;
  flex-direction: row;
  gap: 2rem;
  width: 100%;
  justify-content: flex-start;
  margin-bottom: 1.5rem;
}
.custom-radio-pill {
  display: flex;
  align-items: center;
  border: 2px solid #355E77;
  border-radius: 2rem;
  padding: 0.4rem 1.4rem 0.4rem 1.1rem;
  background: #fff;
  cursor: pointer;
  font-family: 'Mulish', sans-serif;
  font-weight: 700;
  font-size: 1.15rem;
  transition: background 0.2s, border 0.2s;
  position: relative;
  min-width: 0;
  width: auto;
  min-height: 44px;
  user-select: none;
}
.custom-radio-pill.selected {
  background: $seven-color;
  border: 2px solid #355E77;
}
.custom-radio-input {
  display: none;
}
.custom-radio-circle {
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  border-radius: 50%;
  border: 2px solid #355E77;
  background: #fff;
  margin-right: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: border 0.2s, background 0.2s;
}
.custom-radio-pill.selected .custom-radio-circle {
  background: #fff;
  border: 2.5px solid #355E77;
}
.custom-radio-input:checked + .custom-radio-circle::after,
.custom-radio-pill.selected .custom-radio-circle::after {
  content: '';
  display: block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #355E77;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.custom-radio-label {
  font-family: 'Mulish', sans-serif;
  font-family: Mulish;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0%;

  color: #355E77;
  letter-spacing: 0.5px;
  margin-left: 0.1rem;
}
.custom-radio-pill.selected .custom-radio-label {
  color: #355E77;
}
@media (max-width: 700px) {
  .custom-radio-group {
    flex-direction: column;
    gap: 1rem;
    width: 100%;
  }
  .custom-radio-pill {
    min-width: unset;
    width: 100%;
    justify-content: flex-start;
  }
}
// BOTONES DE NAVEGACIÓN
.stepper-nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  font-family: 'Mulish', sans-serif;
  font-weight: 700;
  font-size: 1.1rem;
  border-radius: 2rem;
  padding: 0.6rem 2.2rem;
  min-width: 120px;
  min-height: 44px;
  transition: background 0.18s, color 0.18s, border 0.18s;
  cursor: pointer;
  box-shadow: none;
  outline: none;
  border: 2px solid #355E77;
  background: #fff;
  color: #355E77;
}
.stepper-nav-btn svg {
  width: 1.3em;
  height: 1.3em;
  vertical-align: middle;
}
.stepper-nav-btn:hover {
  background: $seven-color;
  color: #243949;
  border-color: #243949;
}
.stepper-nav-btn.next {
  background: #355E77;
  color: #fff;
  border: none;
  justify-content: center;
}
.stepper-nav-btn.next:hover {
  background: #243949;
  color: #fff;
}
.error-message {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 10px 15px;
  border-radius: 5px;
  margin: 10px 0;
  text-align: center;
  font-weight: 600;
}
</style>
